import { Organization } from 'models/Organization'
import { Purchase, Purchaser } from 'models/Purchase'
import { getAnalyticsInstance } from '../analytics'
import { StudioCampaignResponse } from '../../../models/StudioCampaign'

interface TrackPurchaseEventsContext {
  purchase: Purchase
  campaign: StudioCampaignResponse
  organization: Organization
  purchaser: Purchaser
  walletType: string | null
  dwLoaded: boolean
  paymentSource?: string
}

export const HEAP_PURCHASE_EVENT_NAME = 'Embedded Studio Checkout - Successful Payment'

/**
 * E-Commerce "Purchase" Events are handled in distinct ways for
 * each analytics tool.
 *
 * This function handles all of them.
 */
export const trackPurchaseEvents = async (context: TrackPurchaseEventsContext) => {
  const { purchase, dwLoaded, walletType, paymentSource } = context
  const analytics = getAnalyticsInstance()

  const commonPurchaseEvent = getCommonPurchaseEventBody(context)

  // Tealium and Cross Frame Analytics
  const commonPurchaseEventPromise = analytics.track('purchase', commonPurchaseEvent, {
    plugins: {
      all: false,
      'cross-frame-analytics': true,
      tealium: true,
    },
  })

  // Heap
  const heapPromise = analytics.track(
    HEAP_PURCHASE_EVENT_NAME,
    {
      id: purchase.id,
      revenue: purchase.grandTotal,
      donation_amount: purchase.grandTotal,
      num_attendees: 0,
      raw_currency_code: purchase.currency.displayed_currency_code,
      digitalWalletsPresent: dwLoaded?.toString(),
      paymentRequestButton: walletType,
      paymentSource: paymentSource ?? '',
    },
    {
      plugins: {
        all: false,
        heap: true,
      },
    },
  )

  // Facebook Pixel
  const fbPixelPromise = analytics.track(
    'Purchase',
    {
      content_name: 'Donation',
      content_category: purchase.isCart ? undefined : purchase.items[0].frequency,
      value: purchase.grandTotal,
      currency: purchase.currency.displayed_currency_code,
    },
    {
      plugins: {
        all: false,
        'facebook-pixel': true,
      },
    },
  )

  // Facebook CAPI
  const fbCAPIPromise = analytics.track(
    'Purchase',
    {
      content_name: 'Donation',
      donation_frequency: purchase.isCart ? undefined : purchase.items[0].frequency,
      amount: purchase.grandTotal,
      currency: purchase.currency.displayed_currency_code,
    },
    {
      plugins: {
        all: false,
        'facebook-capi': true,
      },
    },
  )

  return Promise.allSettled([
    withTimeout(10000, commonPurchaseEventPromise),
    withTimeout(10000, heapPromise),
    withTimeout(10000, fbPixelPromise),
    withTimeout(10000, fbCAPIPromise),
  ])
}

const getCommonPurchaseEventBody = ({
  purchase,
  paymentSource,
  campaign,
  organization,
  purchaser,
}: TrackPurchaseEventsContext) => ({
  event: 'purchase',
  transaction_id: purchase.id,
  currency: purchase.currency.displayed_currency_code,
  value: purchase.grandTotal,
  tax: 0,
  shipping: 0,
  affiliation: 'Classy',
  coupon: undefined, // TODO: Update when checkout supports promo codes
  status: 'success',
  created_at: new Date().toUTCString(),
  fees_covered: purchase.dcf,
  has_dedication: purchase.hasDedication,
  refunded_at: undefined,
  billing_zip_code: purchase.zipCode,
  is_gift_aid: false, // Gift Aid not currently supported
  tax_entity_id: 123,
  payment_source: paymentSource,
  ...purchase.currency,
  items: purchase.items.map((purchaseItem) => ({
    item_id: `donation_${purchaseItem.designationId}`,
    item_category: 'donation',
    item_category2: purchaseItem.designationId,
    item_category3: purchaseItem.designationName,
    item_category4: purchaseItem.frequency,
    quantity: purchaseItem.quantity,
    price: purchaseItem.unitPrice,
  })),
  company_match: purchase.companyMatch,
  campaign_id: campaign.campaign_id,
  campaign_name: campaign.campaign_data.name,
  campaign_features: purchase.isCart ? ['cart'] : [],
  campaign_type: 'studio',
  campaign_is_embedded: false,
  campaign_currency_code: campaign.campaign_data.currency_code,
  campaign_default_designation_id: campaign.campaign_data.designation_id,
  campaign_goal: campaign.campaign_data.goal,
  campaign_created_at: campaign.campaign_data.created_at,
  organization_id: organization.id,
  organization_name: organization.name,
  supporter: {
    id: purchaser.id,
    first_name: purchaser.firstName,
    last_name: purchaser.lastName,
    postal_code: purchase.zipCode,
    email_address: purchaser.email,
    company_name: purchase.companyName,
    is_anonymous: purchase.isAnonymous,
  },
})

const withTimeout = (millis: number, promise: Promise<unknown>) => {
  const timeout = new Promise((_resolve, reject) =>
    setTimeout(() => reject(`Timed out after ${millis} ms.`), millis),
  )

  return Promise.race([promise, timeout])
}
